import {Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {DynamicDialogConfig, DynamicDialogRef} from 'primeng/dynamicdialog';
import {AuthService} from '../../services/auth.service';
import {SubscriptionService, trialCodeToDays} from '../../services/subscription.service';
import {UserService} from '../../services/user.service';
import {CurrencyService} from '../../services/currency.service';
import {Partners} from '../../models/PartnersModel';
import {Plans} from '../../models/SubscriptionsModels';
import {take, tap} from 'rxjs/operators';
import {ActivatedRoute, Router} from '@angular/router';
import {environment} from '../../../environments/environment';
import {ToastrService} from 'ngx-toastr';
import {Subscription} from 'rxjs/internal/Subscription';
import Odometer from 'odometer';
import {interval} from 'rxjs/internal/observable/interval';
import {RefersionService} from '../../services/refersion.service';
import {UserModel} from '../../models/User';
import {TrackingParams} from '../../models/Authentication';
import {MixpanelService} from '../../services/mixpanel.service';
import {CouponMeta} from '../signup/plan-selection/plan-selection.component';

declare var Stripe: any;

enum AvailableCurrencies {
    USD = 'USD',
    EUR = 'EUR',
    GBP = 'GBP',
    CAD = 'CAD',
    AUD = 'AUD'
}

const currencyLocales: { [key in AvailableCurrencies]: string } = {
    [AvailableCurrencies.USD]: 'en-US',
    [AvailableCurrencies.EUR]: 'de-DE',
    [AvailableCurrencies.GBP]: 'en-GB',
    [AvailableCurrencies.CAD]: 'en-CA',
    [AvailableCurrencies.AUD]: 'en-AU'
};

export interface CheckoutOptions {
    plan: string;
    partner: Partners;
    coupon?: string;
    onboardingRedirect: boolean;
    updateBilling?: boolean;
    couponMeta?: CouponMeta;
    trackingParams?: TrackingParams;
}

interface PricingDetails {
    basePlanPrice: number;        // Stripe price to convert back to USD
    planPriceDisplay: number;     // display price in selected currency
    discountedPrice?: number;     // discounted price for first year if coupon applies
    trialPrice: number;           // typically 1
    amountToBeCharged: string;    // actual "today" charge, formatted
    selectedCurrency: AvailableCurrencies;
    availableCurrencies: AvailableCurrencies[];
    baseProration?: number; //Proration price default to convert back to USD
    proratedAmount?: number; //display proration price, updates with currency changes
}

interface CouponState {
    coupon: string;
    showCouponInput: boolean;
    discountApplied: boolean;
    discountMessage: string;
    percent_off?: number;
    amount_off?: number;
}

export enum CheckoutScenario {
    FreeTrial = 'FreeTrial',
    TrialNoCoupon = 'TrialNoCoupon',
    TrialWithCoupon = 'TrialWithCoupon',
    NoTrialNoCoupon = 'NoTrialNoCoupon',
    NoTrialWithCoupon = 'NoTrialWithCoupon',
    PartnerViewConfig= 'PartnerViewConfig',
    FreeTrialWithCoupon = 'FreeTrialWithCoupon',
    TrialCodeUrlParam = 'TrialCodeUrlParam',
    UpdateBilling = 'UpdateBilling'
}

enum CardFundingType {
    Credit = 'credit',
    Debit = 'debit',
    Prepaid = 'prepaid',
    Unknown = 'unknown'
}

interface FundingGuardState {
    blockPrepaid: boolean;
    fundingType: CardFundingType | null;
    warning: string;
    paymentIsComplete: boolean;
    paymentHasChangesSinceCheck: boolean;
    isBlocked: boolean;
}
const defaultFundingGuardState = (): FundingGuardState => ({
    blockPrepaid: true,
    fundingType: null,
    warning: '',
    paymentIsComplete: false,
    paymentHasChangesSinceCheck: false,
    isBlocked: false,
});

@Component({
    selector: 'app-checkout-modal',
    templateUrl: './checkout.component.html',
    styleUrls: ['./checkout.component.scss'],
})
export class CheckoutModalComponent implements OnInit, OnDestroy {
    @ViewChild('paymentElement') paymentEl: ElementRef;
    @ViewChild('linkauth') linkAuth: ElementRef;
    @ViewChild('premiumOdometerElem', { static: false }) premiumOdometerElem: ElementRef;

    userCount: number;
    premiumOdometerReady = false;
    private premiumOdometer: any;
    private premiumTickerSub: Subscription;

    private paymentElement: any;
    private lastFundingCheckSignature = '';
    fundingGuard: FundingGuardState = defaultFundingGuardState();

    stripe: any;
    elements: any;
    cardDetailsForm: FormGroup;

    user: UserModel;
    loading: boolean = false;
    selectedPlan: Plans;
    trialDays: number;
    rfsn: string | undefined;
    modalInfo: string[] = [];
    buttonText: string = '';

    metadata: TrackingParams;
    options: CheckoutOptions;
    pricing: PricingDetails = {
        basePlanPrice: 0,
        planPriceDisplay: 0,
        discountedPrice: undefined,
        amountToBeCharged: '',
        trialPrice: 1,
        baseProration: undefined,
        proratedAmount: undefined,
        selectedCurrency: AvailableCurrencies.USD,
        availableCurrencies: [
            AvailableCurrencies.USD,
            AvailableCurrencies.EUR,
            AvailableCurrencies.GBP,
            AvailableCurrencies.CAD,
            AvailableCurrencies.AUD
        ]
    };
    couponState: CouponState = {
        coupon: '',
        showCouponInput: false,
        discountApplied: false,
        discountMessage: ''
    };

    constructor(
        private fb: FormBuilder,
        @Inject(DynamicDialogConfig) public config: DynamicDialogConfig<CheckoutOptions>,
        @Inject(DynamicDialogRef) public dialogRef: DynamicDialogRef,
        private authService: AuthService,
        private subscriptionService: SubscriptionService,
        private userService: UserService,
        private currencyService: CurrencyService,
        private router: Router,
        private toastService: ToastrService,
        private refersionService: RefersionService,
        private route: ActivatedRoute,
        private mixpanel: MixpanelService
    ) {
        const {
            plan,
            partner,
            coupon,
            onboardingRedirect,
            updateBilling,
            trackingParams
        } = config.data as CheckoutOptions;

        this.rfsn = trackingParams.rfsn;
        this.metadata = trackingParams;

        const { couponMeta } = config.data;

        if (couponMeta && couponMeta.valid) {
            this.couponState.coupon = couponMeta.coupon;
            this.couponState.percent_off = couponMeta.percent_off;
            this.couponState.amount_off = couponMeta.amount_off;
            this.couponState.discountApplied = true;
            this.couponState.discountMessage = this.couponState.percent_off
            ? `Coupon applied! You saved ${this.couponState.percent_off.toFixed(0)}%.`
            : `Coupon applied! You saved ${this.formatPrice(this.couponState.amount_off! / 100)}.`;
        } else if (config.data.coupon) {
            this.couponState.coupon = config.data.coupon;
        }
        this.options = {
            plan,
            partner,
            coupon,
            trackingParams: this.metadata,
            onboardingRedirect,
            updateBilling
        };

        this.cardDetailsForm = this.fb.group({
            email: ['', [Validators.required, Validators.email]],
            coupon: ['']
        });
    }

    async ngOnInit(): Promise<void> {
        this.stripe = await Stripe(environment.stripe_key);
        if (this.paymentEl && this.linkAuth) {
            await this.setupElements();
        } else {
            console.error('Element not found');
        }
    }

    ngOnDestroy() {
        this.premiumTickerSub?.unsubscribe();
    }


    setPlan(): void {
        //forcing non-trial plans for users already on a Premium plan and wanting to upgrade to PPlus
        //or for Premium+ wanting to downgrade to Premium
        const isTrial = !this.user.isPaid;
        const body: { partner: string; product: string; isTrial?: boolean } = {
            partner: this.options.partner.name,
            product: this.options.plan
        }
        //append isTrial flag if non-trial plan is enforced
        if (!isTrial){
            body.isTrial = isTrial
        }
        this.subscriptionService.getPlan(body).pipe(
            tap(plan => {
                this.selectedPlan = plan;
                this.pricing.basePlanPrice = plan.recurringPrice / 100;
                this.pricing.planPriceDisplay = this.pricing.basePlanPrice;
                this.trialDays = plan.isTrial ? this.options.partner.trialDays : 0;
                this.setTrialPrice();
                this.recomputePricing();
                this.updateModalUI();
                this.getPremiumUserCount();
                //only calculate proration if Premium upgrades to PPlus and no coupon is applied
                if(!isTrial && !this.couponState.discountApplied){
                    this.getProration();
                }
            })
        ).subscribe({
            error: (error) => {
                this.toastService.error(error, "Error");
            }
        });
    }

    setTrialPrice(exchangeRate: number = 1) {
        this.pricing.trialPrice = parseFloat((exchangeRate).toFixed(2));
    }

    recomputePricing() {
        let renewalPrice = this.pricing.basePlanPrice;
        let firstYearPrice = renewalPrice;
        if (this.couponState.discountApplied) {
            if (this.couponState.percent_off) {
                firstYearPrice = Math.ceil(renewalPrice - (renewalPrice * (this.couponState.percent_off / 100)));
            } else if (this.couponState.amount_off) {
                firstYearPrice = Math.ceil(renewalPrice - (this.couponState.amount_off / 100));
            }
        }
        this.pricing.planPriceDisplay = renewalPrice;
        this.pricing.discountedPrice = firstYearPrice;
        this.updateModalUI();
    }

    private getCheckoutScenario(): CheckoutScenario {
        if(this.options.updateBilling){
            return CheckoutScenario.UpdateBilling
        }
        if (this.trialDays > 0) {
            if (this.options.partner.isFreeTrial && this.couponState.discountApplied && this.pricing.discountedPrice! < this.pricing.planPriceDisplay) {
                return CheckoutScenario.FreeTrialWithCoupon;
            }
            if (this.options.partner.isFreeTrial) {
                return CheckoutScenario.FreeTrial;
            }
            if (this.couponState.discountApplied && this.pricing.discountedPrice! < this.pricing.planPriceDisplay) {
                return CheckoutScenario.TrialWithCoupon;
            }
            if(this.options.partner.isPartnerView) {
                return CheckoutScenario.PartnerViewConfig;
            }
            return CheckoutScenario.TrialNoCoupon;
        } else {
            if (this.metadata.trialCode && !this.couponState.discountApplied){
                return CheckoutScenario.TrialCodeUrlParam;
            }
            if (this.couponState.discountApplied && this.pricing.discountedPrice! < this.pricing.planPriceDisplay) {
                return CheckoutScenario.NoTrialWithCoupon;
            }
            return CheckoutScenario.NoTrialNoCoupon;
        }
    }



    private formatPrice(amount?: number): string {
        const locale = currencyLocales[this.pricing.selectedCurrency];
        if (typeof amount === "number" && !isNaN(amount)) {
            return new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: this.pricing.selectedCurrency,
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            }).format(amount);
        }
        return "--";
    }

    private formatTrialLabel(days: number): string {
        if (days === 90){
            return '3-month'
        }
        if (days === 180) {
            return '6-month';
        }
        if (days === 270){
            return '9-month'
        }
        if (days === 365) {
            return '1-year';
        }
        return `${days}-day`;
    }

    private getProration(){
        this.subscriptionService.getUserProrationDetails(this.selectedPlan.stripePlanId).pipe(take(1)).subscribe({
            next: (res) => {
                //do not prorate a negative amount
                if(res.prorate > 0){
                    this.pricing.baseProration =  res.prorate / 100;
                    this.pricing.proratedAmount = this.pricing.baseProration;
                    this.updateModalUI();
                }
            },
                error: (err) => {
                    this.toastService.error('Error loading price')
                },
            });
    }

    private updateModalUI() {
        const trial = this.formatPrice(this.pricing.trialPrice);
        const firstYear = this.formatPrice(this.pricing.discountedPrice);
        const renewal = this.formatPrice(this.pricing.planPriceDisplay);

        const proratedAmount = this.pricing.proratedAmount ? this.formatPrice(this.pricing.proratedAmount) : undefined;
        const proratedText = proratedAmount
            ? `Pay prorated amount of ${proratedAmount}`
            : undefined;
        const proratedModalInfo = proratedAmount
            ? [`${proratedText}`, "Cancel Anytime", `${renewal}/yr`]
            : ["Cancel Anytime", `${renewal}/yr`]

        const scenario = this.getCheckoutScenario();

        switch (scenario) {
            case CheckoutScenario.UpdateBilling:
                this.modalInfo = ["Update Payment Details"]
                this.buttonText = "Update"
                this.pricing.amountToBeCharged = '';
                break;
            case CheckoutScenario.TrialCodeUrlParam:
                this.modalInfo = [`Try for ${trial}`, "Cancel Anytime", `${renewal}/yr after ${trialCodeToDays(this.metadata.trialCode)} trial`];
                this.buttonText = `Try for ${trial}`;
                this.pricing.amountToBeCharged = trial;
                break;
            case CheckoutScenario.FreeTrialWithCoupon:
                this.modalInfo = ["Try for Free", `${firstYear} first year`, `${renewal}/yr after`];
                this.buttonText = `Start ${this.formatTrialLabel(this.trialDays)} trial for free`;
                this.pricing.amountToBeCharged = "Free";
                break;
            case CheckoutScenario.FreeTrial:
                this.modalInfo = ["Try for Free", "Cancel Anytime", `${renewal}/yr after ${this.formatTrialLabel(this.trialDays)} trial`];
                this.buttonText = `Try for Free`;
                this.pricing.amountToBeCharged = "Free";
                break;
            case CheckoutScenario.TrialWithCoupon:
                this.modalInfo = [`Try for ${trial}`, `${firstYear} first year`, `${renewal}/yr after`];
                this.buttonText = `Start ${this.formatTrialLabel(this.trialDays)} Trial for ${trial}`;
                this.pricing.amountToBeCharged = trial;
                break;
            case CheckoutScenario.TrialNoCoupon:
                this.modalInfo = [`Try for ${trial}`, "Cancel Anytime", `${renewal}/yr after trial`];
                this.buttonText = `Start ${this.formatTrialLabel(this.trialDays)} Trial for ${trial}`;
                this.pricing.amountToBeCharged = trial;
                break;
            case CheckoutScenario.NoTrialWithCoupon:
                this.modalInfo = [`${firstYear} first year`, "Cancel Anytime", `${renewal}/yr after`];
                this.buttonText = `Pay ${firstYear}`;
                this.pricing.amountToBeCharged = firstYear;
                break;
            case CheckoutScenario.PartnerViewConfig:
                this.modalInfo = [`Try for ${trial}`, "Cancel Anytime", `${renewal}/yr after ${this.formatTrialLabel(this.trialDays)} trial`];
                this.buttonText = `Try for $1`;
                this.pricing.amountToBeCharged = firstYear;
                break;
            default:
            case CheckoutScenario.NoTrialNoCoupon:
                this.modalInfo = proratedModalInfo;
                this.buttonText = `Pay ${proratedAmount || renewal}`;
                this.pricing.amountToBeCharged = proratedAmount || renewal;
                break;
        }
    }

    updateCurrencyDisplay(): void {
        if (!this.pricing.basePlanPrice) {
            return;
        }
        if (this.pricing.selectedCurrency === AvailableCurrencies.USD) {
            this.pricing.basePlanPrice = this.selectedPlan.recurringPrice / 100;
            this.pricing.proratedAmount = this.pricing.baseProration;
            this.setTrialPrice(1);
            this.recomputePricing();
            return;
        }
        this.currencyService.getCurrency(this.pricing.selectedCurrency).pipe(
            tap(exchangeRate => {
                const rate = Number(exchangeRate.rate);
                this.pricing.basePlanPrice = parseFloat((this.selectedPlan.recurringPrice / 100 * rate).toFixed(2));
                this.pricing.proratedAmount = parseFloat((this.pricing.baseProration * rate ).toFixed(2))
                this.setTrialPrice(rate);
                this.recomputePricing();
            })
        ).subscribe({
            error: (error) => {
                this.toastService.error(error.error.message, "Error fetching exchange rates");
                console.error('Error fetching currency exchange rate:', error);
            }
        });
    }

    async applyCoupon() {
        this.couponState.coupon = this.cardDetailsForm.get('coupon')?.value || this.couponState.coupon;
        this.couponState.discountApplied = false;
        if (!this.couponState.coupon) {
            this.toastService.error("Please enter a valid coupon", "Coupon Error");
            return;
        }
        this.subscriptionService.checkCoupon(this.couponState.coupon).pipe(
            tap(response => {
                if (!response.valid) {
                    this.toastService.error('Invalid or expired coupon', "Coupon Error");
                    return;
                }
                this.couponState.percent_off = response.percent_off;
                this.couponState.amount_off = response.amount_off;
                this.couponState.discountApplied = true;
                this.couponState.discountMessage = response.percent_off
                    ? `Coupon applied! You saved ${response.percent_off.toFixed(0)}%.`
                    : `Coupon applied! You saved ${this.formatPrice(response.amount_off! / 100)}.`;
                this.recomputePricing();
            })
        ).subscribe({
            error: (error) => {
                this.toastService.error(error.error.message, "Could not apply coupon");
                console.error('Error applying coupon:', error);
            }
        });
    }

    get hideCouponInput(): boolean {
        const scenario = this.getCheckoutScenario();
        return scenario === CheckoutScenario.FreeTrial || scenario === CheckoutScenario.UpdateBilling
    }

    toggleCouponInput() {
        this.couponState.showCouponInput = !this.couponState.showCouponInput;
    }

    async getPremiumUserCount() {
        this.userCount = await this.userService.getPremiumUserCount(this.options.plan);
        this.premiumOdometerReady = true;
        setTimeout(() => {
            this.premiumOdometer = new Odometer({
                el: this.premiumOdometerElem.nativeElement,
                value: this.userCount,
                format: '(,ddd)',
                duration: 800
            });
            this.premiumTickerSub = interval(8000).subscribe(() => {
                const bump = Math.ceil(Math.random() * 3);
                this.userCount += bump;
                this.premiumOdometer.update(this.userCount);
            });
        }, 0);
    }

    async setupElements() {
       await this.subscriptionService.setupIntent(this.user).pipe(
            tap(response => {
                const appearance = {
                    theme: 'stripe',
                    labels: 'floating',
                    variables: {
                        colorPrimary: '#2e8bf8',
                        colorBackground: '#ffffff',
                        colorText: '#30313d',
                        colorDanger: '#df1b41',
                        fontFamily: 'Poppins, sans-serif',
                        spacingUnit: '1.5px',
                        borderRadius: '4px',
                        fontSizeBase: '14px',
                    }
                };
                this.user = response.user;
                this.mixpanel.identify(this.user.id.toString());
                this.mixpanel.track("Checkout Modal Opened", {email: this.user.email})
                const clientSecret = response.setupIntent.client_secret;
                this.elements = this.stripe.elements({
                    appearance,
                    clientSecret,
                    paymentMethodCreation: 'manual'
                });
                const linkAuthenticationElement = this.elements.create('linkAuthentication');
                linkAuthenticationElement.mount(this.linkAuth.nativeElement);
                linkAuthenticationElement.value = this.user.email;
                linkAuthenticationElement.on('change', (event) => {
                    this.user.email = event.value.email;
                });
                const paymentElementOptions = {
                    layout: {
                        type: 'tabs'
                    },
                    defaultValues: {
                        billingDetails: {email: this.user.email}
                    },
                    terms: {
                        card: 'never'
                    }
                };
                this.paymentElement = this.elements.create('payment', paymentElementOptions);
                this.paymentElement.mount(this.paymentEl.nativeElement);
                this.paymentElement.on('change', (event: any) => this.onPaymentElementChange(event));
                if (this.options.updateBilling) {
                    this.updateModalUI();
                    return;
                }
                this.setPlan();
            })
        ).subscribe({
            error: (error) => {
                console.error('Error setting up elements:', error);
            }
        });
    }
    private recomputeFundingGuard(): void {
        this.fundingGuard.isBlocked =
            this.fundingGuard.blockPrepaid &&
            this.fundingGuard.fundingType === 'prepaid' &&
            !this.fundingGuard.paymentHasChangesSinceCheck;
    }

    private resetFundingGuard(): void {
        this.fundingGuard.fundingType = null;
        this.fundingGuard.warning = '';
        this.lastFundingCheckSignature = '';
        this.recomputeFundingGuard();
    }

    private setFundingFromStripe(funding?: string): void {
        const fundingType = (funding || CardFundingType.Unknown) as CardFundingType;
        this.fundingGuard.fundingType = fundingType;

        if (fundingType === CardFundingType.Prepaid) {
            this.fundingGuard.warning =
                'Prepaid card detected. Please use a debit or credit card.';
        } else if (fundingType === CardFundingType.Debit) {
            this.fundingGuard.warning =
                'Debit card detected. If this fails, try a credit card.';
        } else {
            this.fundingGuard.warning = '';
        }

        this.recomputeFundingGuard();
    }

    private onPaymentElementChange(event: any): void {
        this.fundingGuard.paymentIsComplete = !!event?.complete;
        this.fundingGuard.paymentHasChangesSinceCheck = true;

        // If user edits after we previously blocked/warned, immediately unblock + clear warning.
        if (this.fundingGuard.warning || this.fundingGuard.fundingType === CardFundingType.Prepaid) {
            this.resetFundingGuard();
            return;
        }

        this.recomputeFundingGuard();
    }
    private async checkFundingOnSubmit(): Promise<{ decision: 'allow' | 'block' | 'unknown', paymentMethodId?: string }> {
        const { error: submitError } = await this.elements.submit();
        if (submitError) {
            this.toastService.error(submitError.message || 'Please check your payment details.', 'Payment');
            return { decision: 'unknown' };
        }

        const { paymentMethod, error } = await this.stripe.createPaymentMethod({ elements: this.elements });
        if (error || !paymentMethod) {
            return { decision: 'unknown' };
        }

        // we just checked the current input state
        this.fundingGuard.paymentHasChangesSinceCheck = false;

        const funding = (paymentMethod?.card?.funding ?? CardFundingType.Unknown) as CardFundingType;
        this.setFundingFromStripe(funding);

        if (this.fundingGuard.blockPrepaid && funding === CardFundingType.Prepaid) {
            return { decision: 'block' };
        }

        return { decision: 'allow', paymentMethodId: paymentMethod.id };
    }

    async onSubmit() {
        if (this.loading) {
            return;
        }
        this.loading = true;
        let res = null;
        try {
            const funding = await this.checkFundingOnSubmit();

            if (funding.decision === 'block') {
                this.loading = false;
                return;
            }

            res = await this.stripe.confirmSetup({
                elements: this.elements,
                confirmParams: funding.paymentMethodId
                    ? { payment_method: funding.paymentMethodId }
                    : {},
                redirect: 'if_required'
            });
        } catch (err) {
            this.dialogRef.close();
            this.loading = false;
            return;
        }
        if (res.error) {
            this.dialogRef.close();
            this.toastService.error(res.error.code, "Error");
            this.loading = false;
            return;
        }
        if (res.setupIntent.status === 'succeeded') {
            if (this.options.updateBilling) {
                this.subscriptionService.updateCardInfo( res.setupIntent.payment_method)
                    .pipe(take(1))
                    .subscribe({
                    next: () => {
                        this.loading = false;
                        this.dialogRef.close(true);
                        this.router.navigate(['/account/settings']);
                    },
                    error: () => {
                        this.loading = false;
                        this.toastService.error('Error updating card', 'Error');
                    }
                });
                return;
            }

            this.subscriptionService.upgradePlan({
                user: this.user,
                planId: this.selectedPlan.id,
                productId: this.selectedPlan.product.id,
                partnerName: this.options.partner.name,
                paymentMethod: res.setupIntent.payment_method,
                location: this.metadata.location,
                updateBilling: false,
                coupon: this.couponState.coupon,
                origin: 'Signup - Subscribe',
                trialCode: this.metadata.trialCode,
                utm_source: this.metadata.utm_source,
                utm_medium: this.metadata.utm_medium,
                utm_campaign: this.metadata.utm_campaign,
                utm_content: this.metadata.utm_content,
                utm_term: this.metadata.utm_term,
                trafficSource: this.metadata.trafficSource,
                lead_origin: this.metadata.lead_origin,
                upgrade_source: this.metadata.upgrade_source,
                upgrade_campaign: this.metadata.upgrade_campaign,
                upgrade_cohort: this.metadata.upgrade_cohort,
                upgrade_origin: this.metadata.upgrade_origin,
                upgrade_send_id: this.metadata.upgrade_send_id,
                partnerReferenceId: this.metadata.partnerReferenceId
            }).pipe(
                tap(res => {
                    this.loading = false;
                    if(res){
                        this.refersionService.sendCheckoutEvent(this.user.stripeCustomerId);
                    } else {
                        this.dialogRef.close();
                        this.toastService.error("Error charging card, please try a valid credit card", "Error")
                    }
                })
            ).subscribe({
                next: _ => {
                        this.dialogRef.close();
                        if (this.options.updateBilling) {
                            this.router.navigate(['/profile/account']);
                        }
                        if (this.options.onboardingRedirect) {
                            const returnUrl =
                                this.route.snapshot.queryParamMap.get('returnUrl') || '/dashboard';

                            return this.router.navigate(['/onboarding'], {
                                queryParams: {
                                    onboard: true,
                                    plan: this.selectedPlan.product.name,
                                    returnUrl
                                }
                            });
                        }
                        return this.router.navigate(['/dashboard']);
                },
                error: (err) => {
                    this.dialogRef.close();
                    this.loading = false;
                    this.toastService.error("Error charging card, please try a valid credit card", "Error")
                }
            });
        }
    }
}
